// ---------------------------------------------------------------------------
// Microsoft Clarity loader (session recording + heatmaps).
//
// Clarity is NEVER pasted into index.html the way its own "install manually"
// instructions describe. A hardcoded <head> snippet would load Clarity for
// every visitor before anyone agreed to anything, which would contradict the
// consent posture the rest of this folder enforces for Meta and LinkedIn.
//
// Instead it loads ONLY when the consent manager reports marketing is granted,
// and it is torn down the moment consent is withdrawn. Clarity records sessions
// (clicks, scrolls, mouse movement) and sets cookies, so it sits in the same
// risk class as the ad tags and is governed by the same single "marketing"
// consent category.
//
// Network load is gated to production, exactly like the Meta pixel: in
// dev/preview the lifecycle runs and logs to the console so the flow is
// testable, but no real session is sent to Clarity — that keeps dev/QA traffic
// out of the recordings.
//
// A note on sensitivity: Clarity records the rendered page. On the marketing
// site (landing, pricing, /try) that is exactly what we want to watch. If
// recording is ever extended to authenticated app routes that show idea, memo,
// or session content, turn on Clarity's text masking for those routes first so
// confidential invention content is never captured — the app's whole promise is
// confidentiality.
// ---------------------------------------------------------------------------

import { isAnalyticsGranted, subscribeConsent } from './consentManager';

/**
 * The Clarity project id. This is the value from the project's install snippet
 * (`https://www.clarity.ms/tag/<id>`), not a secret — it identifies which
 * Clarity project the sessions land in.
 */
const CLARITY_PROJECT_ID = 'xxbtve4715';
const IS_PROD = import.meta.env.PROD;

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

let scriptInjected = false;
/**
 * Clarity only STARTS once its interaction-gated lifecycle has been installed
 * (see installClarityLifecycle, called from App's first-interaction handler
 * alongside the Meta/Reddit/Ads pixels). Until then `evaluate()` will not inject
 * the tag — this keeps Clarity's ~580ms of script execution out of the initial
 * load window, matching the deferral already applied to every other pixel. The
 * route effect still calls updateClarityRoute on first render, but before arming
 * that only records the path (and can STOP a recording), never starts one — so a
 * cold boot straight onto an authenticated route still never records.
 */
let armed = false;
/** The injected <script>, held so a consent withdrawal can remove it. */
let injectedScript: HTMLScriptElement | null = null;
/** The most recent route, so a consent change can re-decide with the right path. */
let lastPath = typeof window !== 'undefined' ? window.location.pathname : '/';

/**
 * The public marketing surfaces Clarity is allowed to record — an ALLOWLIST, on
 * purpose. Clarity records the rendered page, and this product's core promise is
 * that idea, memo, and session content never leaves the account. An allowlist
 * fails safe: anything not named here (the whole authenticated app, admin, a
 * private route added next year) is never recorded, without anyone having to
 * remember to exclude it. These are exactly the funnel pages where the "traffic
 * doesn't convert" question lives anyway.
 */
const PUBLIC_EXACT = new Set<string>([
  '/',
  '/landing',
  // NOTE: '/try' and '/spark' are deliberately NOT here. Both render the
  // visitor's actual idea text and the live coach conversation, and Clarity
  // records the rendered page. Recording them would capture confidential
  // invention content, which is the one thing this product promises never
  // happens. They stay off the allowlist so those sessions are never recorded.
  // Funnel drop-off on them is measured through the platform's own analytics
  // (pageviews + analytics.track events), not screen recording.
  '/signup',
  '/signin',
  '/pricing',
  '/how-it-works',
  '/about',
  '/contact',
  '/students',
  '/infographic',
  '/marketplace',
  '/terms',
  '/privacy',
  '/security',
  '/cookies',
  '/unsubscribe',
  // Drawings funnel marketing pages. The composer on the landing pages is
  // masked (data-clarity-mask), and the quick start (/start, /resume), which
  // shows a visitor's parts list, is deliberately left off.
  '/invention-concept-drawings',
  '/invention-3d-model',
  '/patent-drawing-cost-calculator',
  '/invention-concept-drawings/sample',
]);
/** Public route families addressed by prefix (blog posts, the sample gallery). */
const PUBLIC_PREFIXES = ['/blog', '/demo/'];

function isPublicMarketingRoute(path: string): boolean {
  const clean = path.split('?')[0].split('#')[0];
  if (PUBLIC_EXACT.has(clean)) return true;
  return PUBLIC_PREFIXES.some((p) => clean === p || clean.startsWith(p));
}

/**
 * Inject the Clarity tag. This is Clarity's own bootstrap, adapted only so the
 * injected <script> is held at module scope (for teardown) and so it falls back
 * to <head> when the document has no existing <script> for `insertBefore` to
 * anchor against — the same fix applied to the Meta and LinkedIn loaders.
 * Idempotent: later calls are no-ops.
 */
function loadClarityScript(): void {
  if (scriptInjected || typeof window === 'undefined') return;
  scriptInjected = true;

  /* eslint-disable */
  (function (c: any, l: Document, a: string, r: string, i: string) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = 'https://www.clarity.ms/tag/' + i;
    injectedScript = t;
    const y = l.getElementsByTagName(r)[0];
    if (y?.parentNode) y.parentNode.insertBefore(t, y);
    else l.head.appendChild(t);
  })(window, document, 'clarity', 'script', CLARITY_PROJECT_ID);
  /* eslint-enable */
}

/**
 * Start Clarity. Safe to call repeatedly. In dev it logs instead of loading, so
 * the consent UX is testable without sending sessions to the real project.
 */
function startClarity(): void {
  if (scriptInjected) return;

  if (!IS_PROD) {
    scriptInjected = true; // latch so the dev log fires once, matching prod
    console.info('[clarity] (dev) start', CLARITY_PROJECT_ID);
    return;
  }

  loadClarityScript();
}

/**
 * The single decision: Clarity records only when marketing consent is granted
 * AND the current route is a public marketing page. Any other combination stops
 * it. Called on every consent change and every route change, so the two inputs
 * are always both honoured.
 */
/**
 * Clarity is ON again (2026-09-29), behind its own Analytics consent switch
 * (separate from Marketing) and on public pages only. It never records inside
 * the signed-in app. Set to false to switch it off everywhere.
 */
const CLARITY_ENABLED = true;

function evaluate(): void {
  const shouldRecord = CLARITY_ENABLED && isAnalyticsGranted() && isPublicMarketingRoute(lastPath);
  // Only start once armed (post-interaction). The stop path stays unguarded so
  // a route change into the authenticated app always tears an active recording
  // down, armed or not.
  if (shouldRecord && !scriptInjected && armed) startClarity();
  else if (!shouldRecord && scriptInjected) unloadClarity();
}

/**
 * Tell Clarity the route changed. Called from the app's location effect. When
 * the user crosses from a public page into the authenticated app, this stops
 * recording before any account content renders.
 */
export function updateClarityRoute(path: string): void {
  lastPath = path;
  evaluate();
}

/**
 * Tear Clarity down on consent withdrawal. Best-effort — code already run
 * cannot be un-run — but it stops further recording, removes the resident
 * library, and expires the cookies Clarity set, so the Cookie preferences
 * screen's promise is kept literally rather than technically.
 */
function unloadClarity(): void {
  if (typeof window === 'undefined') return;

  if (!IS_PROD) {
    // Symmetric with the dev start log, so the public->private route boundary
    // is observable in the console without a real teardown.
    scriptInjected = false;
    console.info('[clarity] (dev) stop');
    return;
  }

  // Ask Clarity to stop recording if the API is present.
  try {
    window.clarity?.('stop');
  } catch {
    /* the tag may not have finished loading; the removals below still apply */
  }

  injectedScript?.remove();
  injectedScript = null;
  delete window.clarity;

  // Clarity's first-party cookies.
  expireCookie('_clck');
  expireCookie('_clsk');

  scriptInjected = false;
}

/** Expire a cookie across the plausible path/domain combinations. */
function expireCookie(name: string): void {
  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const host = window.location.hostname;
  const parts = host.split('.');
  const registrable = parts.length > 2 ? parts.slice(-2).join('.') : host;
  for (const domain of [undefined, host, `.${host}`, `.${registrable}`]) {
    document.cookie =
      `${name}=; expires=${past}; path=/` + (domain ? `; domain=${domain}` : '');
  }
}

/**
 * Install the Clarity lifecycle. Called once at app start:
 *   - If marketing consent is already granted, start immediately.
 *   - Otherwise wait; start the moment consent flips to granted.
 *   - On withdrawal, tear down.
 *
 * Returns an unsubscribe function (used only in dev/StrictMode cleanup).
 */
export function installClarityLifecycle(): () => void {
  // The interaction gate has fired — Clarity is now allowed to start. Decide
  // once against the current route, then re-decide on every consent change.
  // Route changes come in separately via updateClarityRoute.
  armed = true;
  evaluate();
  return subscribeConsent(evaluate);
}
